import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import SupplierDatabaseModal from './SupplierDatabaseModal';

interface RequestAnalysisTabProps {
  request: any;
  requestId: string;
  formatDate: (dateString: string) => string;
  onRequestUpdate?: () => void;
}

const RequestAnalysisTab: React.FC<RequestAnalysisTabProps> = ({
  request,
  requestId,
  formatDate,
  onRequestUpdate
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();

  // Analysis states - now managed internally
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isBraveSearching, setIsBraveSearching] = useState(false);
  const [braveSearchRegion, setBraveSearchRegion] = useState('CH'); // Will be set from request
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<'ai' | 'local' | 'approved' | 'manual'>('ai');
  const [filteredSuppliers, setFilteredSuppliers] = useState<any[]>([]);
  const [filterCriteria, setFilterCriteria] = useState({
    region: '',
    price: '',
    delivery: '',
    emailStatus: 'all'
  });
  const [manualSuppliers, setManualSuppliers] = useState<any[]>([]);
  const [manualSupplierForm, setManualSupplierForm] = useState({
    supplier_name: '',
    supplier_email: '',
    supplier_phone: '',
    supplier_website: '',
    supplier_region: 'TR',
    product_name: '',
    product_brand: '',
    product_description: '',
    product_category: '',
    price: '',
    currency: 'TRY',
    delivery_time: '',
    minimum_order: '',
    payment_terms: '',
    notes: ''
  });
  const [isAddingManual, setIsAddingManual] = useState(false);
  const [isManualAddExpanded, setIsManualAddExpanded] = useState(false);
  
  // Edit modal states
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    type: 'supplier' | 'product';
    data: any;
    supplierIndex: number | null;
    productIndex: number | null;
    supplierType: string;
  }>({
    isOpen: false,
    type: 'supplier',
    data: null,
    supplierIndex: null,
    productIndex: null,
    supplierType: 'ai_suppliers'
  });

  // Email sending states
  const [isEmailMode, setIsEmailMode] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de' | 'tr'>('en');
  
  // Editable supplier fields
  const [editableSupplierName, setEditableSupplierName] = useState('');
  const [editableSupplierEmail, setEditableSupplierEmail] = useState('');
  const [editableAdditionalEmails, setEditableAdditionalEmails] = useState<string[]>([]);
  const [isEditingSupplier, setIsEditingSupplier] = useState(false);
  const [isUpdatingSupplier, setIsUpdatingSupplier] = useState(false);
  const [originalSupplierName, setOriginalSupplierName] = useState('');
  
  // Multi-recipient email state
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [allAvailableEmails, setAllAvailableEmails] = useState<string[]>([]);
  const [selectedCCRecipients, setSelectedCCRecipients] = useState<string[]>([]);
  
  // Refs for form inputs
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  
  // Supplier database modal states
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [selectedSupplierForModal, setSelectedSupplierForModal] = useState<any>(null);
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState<number>(0);

  // Load analysis result when component mounts or request changes
  useEffect(() => {
    if (request?.analysis_result) {
      setAnalysisResult(request.analysis_result);
      setFilteredSuppliers(request.analysis_result.ai_suppliers || []);
    }
    
    if (request?.manual_suppliers) {
      setManualSuppliers(request.manual_suppliers);
    }
    
    // Set Brave Search region from request
    if (request?.region && Array.isArray(request.region) && request.region.length > 0) {
      setBraveSearchRegion(request.region[0]); // Use first region from array
      console.log('🌍 Brave Search region set from request:', request.region[0]);
    } else {
      console.log('🌍 No region found in request, using default: CH');
    }
  }, [request]);

  // Update filtered suppliers when analysis result changes
  useEffect(() => {
    if (analysisResult?.ai_suppliers) {
      setFilteredSuppliers(analysisResult.ai_suppliers);
    }
  }, [analysisResult]);

  // Load email template when selectedSupplier changes and we're in email mode
  useEffect(() => {
    if (isEmailMode && selectedSupplier && token) {
      console.log('🔍 useEffect: Loading template for selectedSupplier:', selectedSupplier.company_name || selectedSupplier.name);
      loadEmailTemplate();
    }
  }, [isEmailMode, selectedSupplier, token]);

  // Analyze request function
  const handleAnalyzeRequest = async () => {
    if (!token || !requestId) return;
    
    try {
      setIsAnalyzing(true);
      const response = await fetch(API_ENDPOINTS.ANALYZE_REQUEST(requestId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAnalysisResult(result.data);
          setFilteredSuppliers(result.data.ai_suppliers || []);
        }
      }
    } catch (error) {
      console.error('Error analyzing request:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Brave Search function
  const handleBraveSearch = async () => {
    setIsBraveSearching(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS}/${requestId}/brave-search?region=${braveSearchRegion}&max_pages=5`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔍 Brave Search response:', result);
      
      if (result.success && result.data) {
        // Get new suppliers from response
        const newSuppliers = result.data.new_suppliers || [];
        const updatedAnalysis = result.data.updated_analysis;
        
        console.log('🔍 Brave Search - New suppliers received:', newSuppliers.length);
        
        if (updatedAnalysis && newSuppliers.length > 0) {
          // PRESERVE EXISTING SUPPLIERS - Only append new ones
          const currentSuppliers = analysisResult?.ai_suppliers || analysisResult?.suppliers || [];
          const updatedSuppliers = [...currentSuppliers, ...newSuppliers];
          
          console.log('🔍 Brave Search - Current suppliers:', currentSuppliers.length);
          console.log('🔍 Brave Search - New suppliers:', newSuppliers.length);
          console.log('🔍 Brave Search - Updated suppliers:', updatedSuppliers.length);
          
          // Update analysis result with preserved data
          const newAnalysisResult = {
            ...analysisResult,  // Keep all existing analysis data
            ...updatedAnalysis,  // Update with new metadata
            ai_suppliers: updatedSuppliers,  // Append new suppliers
            suppliers: updatedSuppliers  // Also update suppliers field
          };
          
          setAnalysisResult(newAnalysisResult);
          setFilteredSuppliers(updatedSuppliers);
          
          console.log('🔍 Brave Search - Final suppliers count:', updatedSuppliers.length);
        } else if (updatedAnalysis) {
          // Fallback: use the complete updated analysis if no new suppliers
          const aiSuppliers = updatedAnalysis.ai_suppliers || updatedAnalysis.suppliers || [];
          
          const newAnalysisResult = {
            ...analysisResult,  // Keep existing data
            ...updatedAnalysis,  // Update with new data
            ai_suppliers: aiSuppliers
          };
          
          setAnalysisResult(newAnalysisResult);
          setFilteredSuppliers(aiSuppliers);
        }
        
        // Show success message
        window.alert(t('common.successfully_added_brave_suppliers', { count: newSuppliers.length }));
      } else {
        console.error('Brave Search failed:', result);
        window.alert(t('common.failed_to_brave_search', { error: result.error || 'Unknown error' }));
      }
    } catch (error) {
      console.error('Error in Brave Search:', error);
      window.alert(t('common.failed_to_brave_search_try_again'));
    } finally {
      setIsBraveSearching(false);
    }
  };

  // Edit functions
  const handleEditSupplier = (supplier: any, index: number, supplierType: string = 'ai_suppliers') => {
    setEditModal({
      isOpen: true,
      type: 'supplier',
      data: supplier,
      supplierIndex: index,
      productIndex: null,
      supplierType: supplierType
    });
  };

  const handleEditProduct = (product: any, supplierIndex: number, productIndex: number, supplierType: string = 'ai_suppliers') => {
    setEditModal({
      isOpen: true,
      type: 'product',
      data: product,
      supplierIndex: supplierIndex,
      productIndex: productIndex,
      supplierType: supplierType
    });
  };

  const handleSaveEdit = async () => {
    try {
      const { type, data, supplierIndex, productIndex, supplierType } = editModal;
      
      let endpoint, requestBody;
      
      if (type === 'supplier') {
        endpoint = `${API_ENDPOINTS.UPDATE_SUPPLIER(requestId)}`;
        requestBody = {
          supplier: data,
          supplier_index: supplierIndex,
          supplier_type: supplierType
        };
      } else {
        endpoint = `${API_ENDPOINTS.UPDATE_PRODUCT(requestId)}`;
        requestBody = {
          product: data,
          supplier_index: supplierIndex,
          product_index: productIndex,
          supplier_type: supplierType
        };
      }

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        // Update local state
        if (type === 'supplier' && supplierIndex !== null) {
          const updatedSuppliers = [...filteredSuppliers];
          updatedSuppliers[supplierIndex] = data;
          setFilteredSuppliers(updatedSuppliers);
          
          // Also update analysis result
          if (analysisResult) {
            const updatedAnalysisResult = { ...analysisResult };
            updatedAnalysisResult[supplierType][supplierIndex] = data;
            setAnalysisResult(updatedAnalysisResult);
          }
        } else if (type === 'product' && supplierIndex !== null && productIndex !== null) {
          // Update product within supplier
          const updatedSuppliers = [...filteredSuppliers];
          updatedSuppliers[supplierIndex].products[productIndex] = data;
          setFilteredSuppliers(updatedSuppliers);
          
          // Also update analysis result
          if (analysisResult) {
            const updatedAnalysisResult = { ...analysisResult };
            updatedAnalysisResult[supplierType][supplierIndex].products[productIndex] = data;
            setAnalysisResult(updatedAnalysisResult);
          }
        }
        
        // Show detailed success popup
        if (type === 'supplier') {
          const supplierDetails = `Supplier updated successfully!\n\nCompany: ${data.company_name || data.name || 'Unknown'}\nEmail: ${data.email || 'No email'}\nPhone: ${data.phone || 'No phone'}\nWebsite: ${data.website || 'No website'}\nAddress: ${data.address || 'No address'}\n\nChanges have been saved to the database.`;
          window.alert(supplierDetails);
        } else {
          const productDetails = `Product updated successfully!\n\nProduct: ${data.product_name || 'Unknown'}\nBrand: ${data.brand || 'No brand'}\nPrice: ${data.price || 'No price'}\nMinimum Order: ${data.minimum_order || 'No minimum order'}\nAvailability: ${data.availability || 'No availability'}\n\nChanges have been saved to the database.`;
          window.alert(productDetails);
        }
        setEditModal({ isOpen: false, type: 'supplier', data: null, supplierIndex: null, productIndex: null, supplierType: 'ai_suppliers' });
        
        // Refresh the page to get updated data from backend
        window.location.reload();
      } else {
        window.alert('Failed to update');
      }
    } catch (error) {
      console.error('Error updating:', error);
      window.alert('Error updating');
    }
  };

  // Load more suppliers function
  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.ANALYZE_REQUEST_MORE(requestId)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('🔍 More analysis response:', result);
      console.log('🔍 Updated analysis:', result.data?.updated_analysis);
      console.log('🔍 New suppliers:', result.data?.new_suppliers);
      
      if (result.success && result.data) {
        // Get new suppliers from response
        const newSuppliers = result.data.new_suppliers || [];
        const updatedAnalysis = result.data.updated_analysis;
        
        console.log('🔍 New suppliers received:', newSuppliers.length);
        console.log('🔍 Updated analysis received:', updatedAnalysis);
        
        if (updatedAnalysis && newSuppliers.length > 0) {
          // PRESERVE EXISTING SUPPLIERS - Only append new ones
          const currentSuppliers = analysisResult?.ai_suppliers || analysisResult?.suppliers || [];
          const updatedSuppliers = [...currentSuppliers, ...newSuppliers];
          
          console.log('🔍 Current suppliers count:', currentSuppliers.length);
          console.log('🔍 New suppliers count:', newSuppliers.length);
          console.log('🔍 Updated suppliers count:', updatedSuppliers.length);
          
          // Update analysis result with preserved data
          const newAnalysisResult = {
            ...analysisResult,  // Keep all existing analysis data
            ...updatedAnalysis,  // Update with new metadata
            ai_suppliers: updatedSuppliers,  // Append new suppliers
            suppliers: updatedSuppliers  // Also update suppliers field
          };
          
          setAnalysisResult(newAnalysisResult);
          setFilteredSuppliers(updatedSuppliers);
          
          console.log('🔍 Final suppliers count after update:', updatedSuppliers.length);
        } else if (updatedAnalysis) {
          // Fallback: use the complete updated analysis if no new suppliers
          const aiSuppliers = updatedAnalysis.ai_suppliers || updatedAnalysis.suppliers || [];
          
          const newAnalysisResult = {
            ...analysisResult,  // Keep existing data
            ...updatedAnalysis,  // Update with new data
            ai_suppliers: aiSuppliers
          };
          
          setAnalysisResult(newAnalysisResult);
          setFilteredSuppliers(aiSuppliers);
        }
        
        // Show success message
        window.alert(t('common.successfully_added_new_suppliers', { count: newSuppliers.length }));
      } else {
        console.error('More analysis failed:', result);
        window.alert(t('common.failed_to_load_more_suppliers', { error: result.error || 'Unknown error' }));
      }
    } catch (error) {
      console.error('Error loading more suppliers:', error);
      window.alert(t('common.failed_to_load_more_suppliers_try_again'));
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Manual form change handler
  const handleManualFormChange = (field: string, value: string) => {
    setManualSupplierForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add manual supplier function
  const addManualSupplier = async () => {
    if (!manualSupplierForm.supplier_name || !manualSupplierForm.product_name || !manualSupplierForm.price) {
      window.alert(t('common.please_fill_required_fields'));
      return;
    }

    try {
      setIsAddingManual(true);
      // Create supplier object with same structure as ai_suppliers
      const newSupplier = {
        company_name: manualSupplierForm.supplier_name,
        website: manualSupplierForm.supplier_website,
        email: manualSupplierForm.supplier_email,
        phone: manualSupplierForm.supplier_phone,
        region: manualSupplierForm.supplier_region,
        products: [
          {
            name: manualSupplierForm.product_name,
            description: manualSupplierForm.product_description,
            price: manualSupplierForm.price,
            link: manualSupplierForm.supplier_website
          }
        ],
        rating: 0, // Manual suppliers don't have rating initially
        delivery_time: manualSupplierForm.delivery_time,
        additional_emails: [],
        // Additional fields for manual suppliers
        product_brand: manualSupplierForm.product_brand,
        product_category: manualSupplierForm.product_category,
        currency: manualSupplierForm.currency,
        minimum_order: manualSupplierForm.minimum_order,
        payment_terms: manualSupplierForm.payment_terms,
        notes: manualSupplierForm.notes,
        // Metadata
        id: `manual_${Date.now()}`,
        added_at: new Date().toISOString(),
        added_by: 'admin'
      };
      
      const response = await fetch(`${API_ENDPOINTS.REQUESTS}/${requestId}/manual-suppliers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ supplier: newSupplier })
      });

      if (response.ok) {
        setManualSuppliers(prev => [...prev, newSupplier]);
        setManualSupplierForm({
          supplier_name: '',
          supplier_email: '',
          supplier_phone: '',
          supplier_website: '',
          supplier_region: 'TR',
          product_name: '',
          product_brand: '',
          product_description: '',
          product_category: '',
          price: '',
          currency: 'TRY',
          delivery_time: '',
          minimum_order: '',
          payment_terms: '',
          notes: ''
        });
        console.log('✅ Manual supplier added successfully');
        
        // Refresh request data in parent component
        if (onRequestUpdate) {
          onRequestUpdate();
        }
      } else {
        throw new Error('Failed to save manual supplier');
      }
    } catch (error) {
      console.error('Error adding manual supplier:', error);
    } finally {
      setIsAddingManual(false);
    }
  };

  // Remove manual supplier function
  const removeManualSupplier = async (supplierId: string) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS}/${requestId}/manual-suppliers`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ supplier_id: supplierId })
      });

      if (response.ok) {
        setManualSuppliers(prev => prev.filter(s => s.id !== supplierId));
        console.log('✅ Manual supplier removed successfully');
      } else {
        throw new Error('Failed to remove manual supplier');
      }
    } catch (error) {
      console.error('Error removing manual supplier:', error);
      window.alert('Failed to remove manual supplier');
    }
  };

  // Filter suppliers function
  const handleFilterSuppliers = () => {
    if (!analysisResult) return;
    
    let filtered = [...(analysisResult.ai_suppliers || [])];
    
    if (filterCriteria.region) {
      filtered = filtered.filter(supplier => 
        supplier.supplier_region?.toLowerCase().includes(filterCriteria.region.toLowerCase())
      );
    }
    
    if (filterCriteria.price) {
      const priceValue = parseFloat(filterCriteria.price);
      filtered = filtered.filter(supplier => 
        parseFloat(supplier.unit_price) <= priceValue
      );
    }
    
    if (filterCriteria.delivery) {
      filtered = filtered.filter(supplier => 
        supplier.delivery_time?.toLowerCase().includes(filterCriteria.delivery.toLowerCase())
      );
    }
    
    setFilteredSuppliers(filtered);
  };

  // Clear filters function
  const clearFilters = () => {
    setFilterCriteria({
      region: '',
      price: '',
      delivery: '',
      emailStatus: 'all'
    });
    setFilteredSuppliers(analysisResult?.ai_suppliers || []);
  };

  // Action completed handler
  const handleActionCompleted = () => {
    // Refresh the analysis result
    handleAnalyzeRequest();
  };

  // Add supplier to database - open modal
  const handleAddToDatabase = (supplier: any, index: number, supplierType: string = 'ai') => {
    setSelectedSupplierForModal({...supplier, supplierType, supplierIndex: index});
    setSelectedSupplierIndex(index);
    setIsSupplierModalOpen(true);
  };

  // Function to fetch approved supplier data
  const fetchApprovedSupplierData = async (supplierName: string) => {
    console.log('🔍 fetchApprovedSupplierData called with supplierName:', supplierName);
    try {
      const response = await fetch(API_ENDPOINTS.SUPPLIERS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🔍 API response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('🔍 API result:', result);
        
        if (result.success && result.data) {
          console.log('🔍 Found suppliers:', result.data.length);
          
          // Find the supplier by name
          const supplier = result.data.find((s: any) => s.name === supplierName);
          console.log('🔍 Found supplier:', supplier);
          
          if (supplier) {
            let mainEmail = '';
            let additionalEmails: string[] = [];
            
            console.log('🔍 Supplier emails field:', supplier.emails);
            console.log('🔍 Supplier email field:', supplier.email);
            
            // Check if supplier has emails array (new format)
            if (supplier.emails && Array.isArray(supplier.emails) && supplier.emails.length > 0) {
              // Use emails array: first is main, rest are additional
              mainEmail = supplier.emails[0];
              additionalEmails = supplier.emails.slice(1);
              console.log('🔍 Using emails array - mainEmail:', mainEmail, 'additionalEmails:', additionalEmails);
            } else if (supplier.email) {
              // Fallback to single email field
              mainEmail = supplier.email;
              additionalEmails = [];
              console.log('🔍 Using single email field - mainEmail:', mainEmail);
            }
            
            console.log('🔍 Final mainEmail:', mainEmail);
            console.log('🔍 Final additionalEmails:', additionalEmails);
            
            if (mainEmail) {
              console.log('🔍 Setting editableSupplierName to:', supplier.name || supplier.company_name);
              setEditableSupplierName(supplier.name || supplier.company_name || '');
              console.log('🔍 Setting editableSupplierEmail to:', mainEmail);
              setEditableSupplierEmail(mainEmail);
              console.log('🔍 Setting editableAdditionalEmails to:', additionalEmails);
              setEditableAdditionalEmails(additionalEmails);
              
              // Update available emails and selected recipients
              const allEmails = [mainEmail, ...additionalEmails];
              console.log('🔍 Setting allAvailableEmails to:', allEmails);
              setAllAvailableEmails(allEmails);
              console.log('🔍 Setting selectedRecipients to:', [mainEmail]);
              setSelectedRecipients([mainEmail]);
            } else {
              console.log('❌ No mainEmail found!');
            }
          } else {
            console.log('❌ Supplier not found!');
          }
        } else {
          console.log('❌ API result not successful or no suppliers');
        }
      } else {
        console.log('❌ API response not ok');
      }
    } catch (error) {
      console.error('❌ Error fetching approved supplier data:', error);
    }
  };

  // Email sending functions
  const handleSendEmail = (supplier: any, index: number, supplierType: string = 'ai') => {
    console.log('📧 Opening email mode for supplier:', supplier, 'Type:', supplierType);
    
    setSelectedSupplier(supplier);
    setEditableSupplierName(supplier.company_name || supplier.name || '');
    setEditableSupplierEmail(supplier.email || '');
    setEditableAdditionalEmails(supplier.additional_emails || []);
    setOriginalSupplierName(supplier.company_name || supplier.name || '');
    // Create subject with mail IDs
    console.log('🔍 DEBUG: Request data for mail IDs:', {
      company_mail_id: request.company_mail_id,
      mail_id: request.mail_id,
      product_name: request.product_name
    });
    
    const companyMailId = request.company_mail_id ? String(request.company_mail_id).padStart(2, '0') : '11';
    const requestMailId = request.mail_id ? String(request.mail_id).padStart(3, '0') : '010';
    const subjectWithMailIds = `${companyMailId}${requestMailId} - ${request.product_name}`;
    
    console.log('🔍 DEBUG: Mail IDs for subject:', {
      companyMailId,
      requestMailId,
      subjectWithMailIds
    });
    
    setEmailSubject(subjectWithMailIds);
    setEmailContent('');
    setError(null);
    setIsEmailMode(true);
    
    // Store supplier type and index for later use
    setSelectedSupplier({...supplier, supplierType, supplierIndex: index});
    
    // Set up recipient emails
    const mainEmail = supplier.email || '';
    const additionalEmails = supplier.additional_emails || [];
    const allEmails = [mainEmail, ...additionalEmails].filter(email => email && email.trim() !== '');
    
    console.log('📧 Setting up recipients:', {
      mainEmail,
      additionalEmails,
      allEmails
    });
    
    setAllAvailableEmails(allEmails);
    setSelectedRecipients(mainEmail ? [mainEmail] : []);
    
    // Initialize CC recipients (empty for now, can be populated later)
    setSelectedCCRecipients([]);
    
    console.log('📧 Set editable values:', {
      editableSupplierName: supplier.company_name || supplier.name || '',
      editableSupplierEmail: supplier.email || ''
    });
    
    // Email modal opened - no popup needed
    
    // If it's an approved supplier, fetch additional data
    if (request.selected_approved_suppliers && request.selected_approved_suppliers.includes(supplier.company_name || supplier.name)) {
      fetchApprovedSupplierData(supplier.company_name || supplier.name);
    }
  };

  const handleBackToAnalysis = () => {
    setIsEmailMode(false);
    setSelectedSupplier(null);
    setEmailSubject('');
    setEmailContent('');
    setError(null);
    setIsEditingSupplier(false);
    setEditableSupplierName('');
    setEditableSupplierEmail('');
    setEditableAdditionalEmails([]);
    setSelectedRecipients([]);
    setAllAvailableEmails([]);
  };

  // Helper function to get supplier emails
  const getSupplierEmails = (requestData: any, supplierIndex: number, useEditable: boolean = false) => {
    if (!requestData) return [];
    
    if (useEditable) {
      return [editableSupplierEmail, ...editableAdditionalEmails].filter(email => email && email.trim() !== '');
    }
    
    // For AI suppliers
    if (requestData.analysis_result?.ai_suppliers && supplierIndex < requestData.analysis_result.ai_suppliers.length) {
      const supplier = requestData.analysis_result.ai_suppliers[supplierIndex];
      return [supplier.email].filter(email => email && email.trim() !== '');
    }
    
    // For local suppliers
    if (requestData.analysis_result?.local_suppliers && supplierIndex < requestData.analysis_result.local_suppliers.length) {
      const supplier = requestData.analysis_result.local_suppliers[supplierIndex].supplier;
      return [supplier.email].filter(email => email && email.trim() !== '');
    }
    
    return [];
  };

  // Handle supplier info editing (old function removed - using new edit modal system)

  const handleCancelEditSupplier = () => {
    setIsEditingSupplier(false);
    setEditableSupplierName(originalSupplierName);
    setEditableSupplierEmail(selectedSupplier?.email || '');
    setEditableAdditionalEmails([]);
  };

  // Additional email management functions
  const handleUpdateAdditionalEmail = (index: number, value: string) => {
    const newEmails = [...editableAdditionalEmails];
    newEmails[index] = value;
    setEditableAdditionalEmails(newEmails);
  };

  const handleRemoveAdditionalEmail = (index: number) => {
    const newEmails = editableAdditionalEmails.filter((_, i) => i !== index);
    setEditableAdditionalEmails(newEmails);
  };

  const handleAddAdditionalEmail = () => {
    setEditableAdditionalEmails([...editableAdditionalEmails, '']);
  };

  const handleSaveSupplier = async () => {
    if (!selectedSupplier || !token) return;
    
    // Get current values from refs or fallback to state
    const currentName = nameInputRef.current?.value || editableSupplierName;
    const currentEmail = emailInputRef.current?.value || editableSupplierEmail;
    
    console.log('🔍 Debug form inputs:', {
      nameInputRef: nameInputRef.current,
      emailInputRef: emailInputRef.current,
      nameInputValue: nameInputRef.current?.value,
      emailInputValue: emailInputRef.current?.value,
      editableSupplierName: editableSupplierName,
      editableSupplierEmail: editableSupplierEmail
    });
    
    console.log('💾 Saving supplier with data:', {
      name: currentName,
      email: currentEmail,
      additional_emails: editableAdditionalEmails,
      selectedSupplier: selectedSupplier
    });
    
    setIsUpdatingSupplier(true);
    try {
      const requestBody = {
        supplier_name: currentName,
        supplier_email: currentEmail,
        additional_emails: editableAdditionalEmails
      };
      
      // Determine the correct endpoint based on supplier type
      let supplierIndex = selectedSupplier.supplierIndex !== undefined ? selectedSupplier.supplierIndex.toString() : '0';
      
      // For manual suppliers, we need to calculate the local index within manual_suppliers array
      if (selectedSupplier.supplierType === 'manual') {
        // Find the local index of this manual supplier
        const manualSuppliers = request?.manual_suppliers || [];
        const globalIndex = parseInt(supplierIndex);
        const localIndex = globalIndex - (request?.ai_suppliers?.length || 0);
        supplierIndex = localIndex.toString();
        console.log(`🔧 DEBUG: Manual supplier - Global index: ${globalIndex}, Local index: ${localIndex}, Manual suppliers count: ${manualSuppliers.length}`);
      }
      
      const endpoint = selectedSupplier.supplierType === 'manual' 
        ? `${API_ENDPOINTS.REQUESTS}/${requestId}/manual-supplier/${supplierIndex}/update-info`
        : API_ENDPOINTS.UPDATE_SUPPLIER_INFO(requestId, supplierIndex);
      
      console.log('📤 Sending request body:', requestBody);
      console.log('📤 Request URL:', endpoint);
      console.log('📤 Supplier type:', selectedSupplier.supplierType);
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setOriginalSupplierName(editableSupplierName);
          setIsEditingSupplier(false);
          // Update selected supplier data
          setSelectedSupplier({
            ...selectedSupplier,
            company_name: editableSupplierName,
            name: editableSupplierName,
            email: editableSupplierEmail
          });
          
          // Refresh the page to get updated data from backend
          window.location.reload();
        } else {
          setError(result.message || 'Failed to update supplier info');
        }
      } else {
        setError('Failed to update supplier info');
      }
    } catch (error) {
      console.error('Error updating supplier info:', error);
      setError('Failed to update supplier info');
    } finally {
      setIsUpdatingSupplier(false);
    }
  };


  // Handle recipient selection
  const handleRecipientToggle = (email: string) => {
    setSelectedRecipients(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  const handleCCRecipientToggle = (email: string) => {
    setSelectedCCRecipients(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  const handleSelectAllRecipients = () => {
    setSelectedRecipients([...allAvailableEmails]);
  };

  const handleDeselectAllRecipients = () => {
    setSelectedRecipients([]);
  };

  const handleSelectAllCCRecipients = () => {
    setSelectedCCRecipients([...allAvailableEmails]);
  };

  const handleDeselectAllCCRecipients = () => {
    setSelectedCCRecipients([]);
  };

  // Handle language change
  const handleChangeLanguage = () => {
    const newLanguage = currentLanguage === 'en' ? 'de' : 
                       currentLanguage === 'de' ? 'tr' : 'en';
    console.log('🔄 Changing language from', currentLanguage, 'to', newLanguage);
    setCurrentLanguage(newLanguage);
    // Load template with new language immediately
    loadEmailTemplateWithLanguage(newLanguage);
  };

  const loadEmailTemplate = async () => {
    console.log('🔍 loadEmailTemplate called with:', { 
      selectedSupplier: selectedSupplier?.company_name || selectedSupplier?.name, 
      token: !!token,
      requestId,
      supplierIndex: selectedSupplier?.index 
    });
    
    if (!selectedSupplier || !token) {
      console.log('❌ Missing selectedSupplier or token');
      return;
    }
    
    setIsLoadingTemplate(true);
    setError(null);
    
    try {
      // Add language parameter to the request
      const supplierIndex = selectedSupplier.supplierIndex !== undefined ? selectedSupplier.supplierIndex.toString() : '0';
      const baseUrl = API_ENDPOINTS.GET_EMAIL_TEMPLATE(requestId, supplierIndex);
      const url = new URL(baseUrl);
      url.searchParams.append('language', currentLanguage);
      
      console.log('🔍 Fetching template from:', url.toString(), 'with language:', currentLanguage);
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 Template response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('🔍 Template result:', result);
        
        if (result.success && result.data) {
          console.log('🔍 Template subject from backend:', result.data.subject);
          console.log('🔍 Setting email content:', result.data.content);
          
          // Don't override subject with mail IDs - keep the mail ID subject we set earlier
          // setEmailSubject(result.data.subject || `Procurement Request - ${request.product_name}`);
          setEmailContent(result.data.content || '');
        } else {
          console.log('❌ Template result not successful:', result.message);
          setError(result.message || 'Failed to load email template');
        }
      } else {
        console.log('❌ Template response not ok:', response.status);
        setError('Failed to load email template');
      }
    } catch (error) {
      console.error('❌ Error loading email template:', error);
      setError('Failed to load email template');
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const loadEmailTemplateWithLanguage = async (language: 'en' | 'de' | 'tr') => {
    console.log('🔍 loadEmailTemplateWithLanguage called with:', { 
      selectedSupplier: selectedSupplier?.company_name || selectedSupplier?.name, 
      token: !!token,
      requestId,
      supplierIndex: selectedSupplier?.index,
      language
    });
    
    if (!selectedSupplier || !token) {
      console.log('❌ Missing selectedSupplier or token');
      return;
    }
    
    setIsLoadingTemplate(true);
    setError(null);
    
    try {
      // Add language parameter to the request
      const baseUrl = API_ENDPOINTS.GET_EMAIL_TEMPLATE(requestId, selectedSupplier.index?.toString() || '0');
      const url = new URL(baseUrl);
      url.searchParams.append('language', language);
      
      console.log('🔍 Fetching template from:', url.toString(), 'with language:', language);
      
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 Template response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('🔍 Template result:', result);
        
        if (result.success && result.data) {
          console.log('🔍 Template subject from backend:', result.data.subject);
          console.log('🔍 Setting email content:', result.data.content);
          
          // Don't override subject with mail IDs - keep the mail ID subject we set earlier
          // setEmailSubject(result.data.subject || `Procurement Request - ${request.product_name}`);
          setEmailContent(result.data.content || '');
        } else {
          console.log('❌ Template result not successful:', result.message);
          setError(result.message || 'Failed to load email template');
        }
      } else {
        console.log('❌ Template response not ok:', response.status);
        setError('Failed to load email template');
      }
    } catch (error) {
      console.error('❌ Error loading email template:', error);
      setError('Failed to load email template');
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const sendEmail = async () => {
    if (!selectedSupplier || !emailSubject || !emailContent || !token || selectedRecipients.length === 0) return;
    
    // Show confirmation popup
    const ccText = selectedCCRecipients.length > 0 ? `\nCC: ${selectedCCRecipients.join(', ')}` : '';
    const confirmationMessage = `Are you sure you want to send this email?\n\nTo: ${selectedRecipients.join(', ')}${ccText}\nSubject: ${emailSubject}\nSupplier: ${editableSupplierName || selectedSupplier.company_name || selectedSupplier.name}\n\nThis will send the email to the selected recipients.`;
    if (!window.confirm(confirmationMessage)) {
      return;
    }
    
    setIsSending(true);
    setError(null);
    
    try {
      // Use supplier index or fallback to 0 for manual suppliers
      const supplierIndex = selectedSupplier.supplierIndex !== undefined ? selectedSupplier.supplierIndex.toString() : '0';
      const response = await fetch(API_ENDPOINTS.SEND_SUPPLIER_EMAIL(requestId, supplierIndex), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email_subject: emailSubject,
          email_content: emailContent,
          language: currentLanguage,
          recipient_emails: selectedRecipients,
          cc_recipients: selectedCCRecipients,
          supplier_name: editableSupplierName || selectedSupplier.company_name || selectedSupplier.name,
          supplier_email: editableSupplierEmail || selectedSupplier.email
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Show detailed success popup
          const ccText = selectedCCRecipients.length > 0 ? `\nCC: ${selectedCCRecipients.join(', ')}` : '';
          const successMessage = `Email sent successfully!\n\nTo: ${selectedRecipients.join(', ')}${ccText}\nSubject: ${emailSubject}\nSupplier: ${editableSupplierName || selectedSupplier.company_name || selectedSupplier.name}\n\nYou can view the conversation in the Mails tab.`;
          window.alert(successMessage);
          handleBackToAnalysis();
          
          // Refresh the page to get updated data from backend
          window.location.reload();
        } else {
          setError(result.message || t('common.email_send_failed'));
        }
      } else {
        setError(t('common.email_send_failed'));
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setError(t('common.email_send_failed'));
    } finally {
      setIsSending(false);
    }
  };

  // Email mode UI - Copy of EmailModal
  if (isEmailMode) {
    return (
      <div className="email-modal-overlay">
        <div className="email-modal-content">
          <div className="email-modal-header">
            <h2>{t('components.email_modal.title')}</h2>
            <button className="close-btn" onClick={handleBackToAnalysis}>×</button>
          </div>

          <div className="email-modal-body">
            <div className="supplier-info">
              <div className="supplier-header">
                <h3>{t('components.email_modal.supplier_details')}</h3>
                <button 
                  className="edit-supplier-btn"
                  onClick={() => setIsEditingSupplier(!isEditingSupplier)}
                  disabled={isUpdatingSupplier}
                >
                  {isEditingSupplier ? t('components.email_modal.cancel_edit') : t('common.edit')}
                </button>
              </div>
              
              {isEditingSupplier ? (
                <div className="supplier-edit-form">
                  <div className="form-group">
                    <label htmlFor="supplier-name">{t('common.name')} *</label>
                    <input
                      id="supplier-name"
                      ref={nameInputRef}
                      type="text"
                      value={editableSupplierName}
                      onChange={(e) => setEditableSupplierName(e.target.value)}
                      placeholder={t('components.email_modal.enter_supplier_name')}
                      className="supplier-input"
                      disabled={isUpdatingSupplier}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="supplier-email">{t('common.email')} *</label>
                    <input
                      id="supplier-email"
                      ref={emailInputRef}
                      type="email"
                      value={editableSupplierEmail}
                      onChange={(e) => setEditableSupplierEmail(e.target.value)}
                      placeholder={t('components.email_modal.enter_supplier_email')}
                      className="supplier-input"
                      disabled={isUpdatingSupplier}
                    />
                  </div>
                  
                  {/* Additional Emails Section */}
                  <div className="form-group">
                    <div className="additional-emails-header">
                      <label>Additional Emails</label>
                      <button
                        type="button"
                        className="add-email-btn"
                        onClick={handleAddAdditionalEmail}
                        disabled={isUpdatingSupplier}
                      >
                        + Add Email
                      </button>
                    </div>
                    
                    {editableAdditionalEmails.map((email, index) => (
                      <div key={index} className="additional-email-row">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => handleUpdateAdditionalEmail(index, e.target.value)}
                          placeholder="Enter additional email address"
                          className="supplier-input additional-email-input"
                          disabled={isUpdatingSupplier}
                        />
                        <button
                          type="button"
                          className="remove-email-btn"
                          onClick={() => handleRemoveAdditionalEmail(index)}
                          disabled={isUpdatingSupplier}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    
                    {editableAdditionalEmails.length === 0 && (
                      <p className="no-additional-emails">
                        No additional emails added yet. Click "Add Email" to add more email addresses.
                      </p>
                    )}
                    
                    <button
                      type="button"
                      className="add-additional-email-btn"
                      onClick={handleAddAdditionalEmail}
                      disabled={isUpdatingSupplier}
                    >
                      ➕ Add Email
                    </button>
                  </div>
                  <div className="supplier-edit-actions">
                    <button
                      className="update-supplier-btn"
                      onClick={handleSaveSupplier}
                      disabled={isUpdatingSupplier || !editableSupplierName || !editableSupplierName.trim() || !editableSupplierEmail || !editableSupplierEmail.trim()}
                    >
                      {isUpdatingSupplier ? t('common.update') + '...' : t('components.email_modal.update_supplier')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supplier-display">
                  <p><strong>{t('common.name')}:</strong> {editableSupplierName}</p>
                  <p><strong>{t('common.email')}:</strong> {editableSupplierEmail}</p>
                  {editableAdditionalEmails.length > 0 && (
                    <div className="additional-emails-display">
                      <p><strong>Additional Emails:</strong></p>
                      <ul>
                        {editableAdditionalEmails.map((email, index) => (
                          <li key={index}>{email}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              <div className="language-info">
                <span className="language-badge">
                  {currentLanguage === 'en' ? t('components.email_modal.english') : 
                   currentLanguage === 'de' ? t('components.email_modal.german') : 
                   t('components.email_modal.turkish')}
                </span>
              </div>
            </div>

            {/* Multi-recipient selection */}
            {allAvailableEmails.length > 0 && (
              <div className="recipient-selection">
                <div className="recipient-selection-header">
                  <h4>Select Recipients</h4>
                  <div className="recipient-actions">
                    <button 
                      type="button" 
                      className="select-all-btn"
                      onClick={handleSelectAllRecipients}
                    >
                      Select All TO
                    </button>
                    <button 
                      type="button" 
                      className="deselect-all-btn"
                      onClick={handleDeselectAllRecipients}
                    >
                      Deselect All TO
                    </button>
                    <button 
                      type="button" 
                      className="select-all-btn"
                      onClick={handleSelectAllCCRecipients}
                    >
                      Select All CC
                    </button>
                    <button 
                      type="button" 
                      className="deselect-all-btn"
                      onClick={handleDeselectAllCCRecipients}
                    >
                      Deselect All CC
                    </button>
                  </div>
                </div>
                <div className="recipient-list">
                  {allAvailableEmails.map((email, index) => (
                    <div key={index} className="recipient-item">
                      <div className="email-address">{email}</div>
                      <div className="recipient-options">
                        <label className="recipient-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedRecipients.includes(email)}
                            onChange={() => handleRecipientToggle(email)}
                          />
                          <span className="checkmark"></span>
                          <span className="checkbox-label">TO</span>
                        </label>
                        <label className="recipient-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedCCRecipients.includes(email)}
                            onChange={() => handleCCRecipientToggle(email)}
                          />
                          <span className="checkmark"></span>
                          <span className="checkbox-label">CC</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="selected-count">
                  TO: {selectedRecipients.length} of {allAvailableEmails.length} | CC: {selectedCCRecipients.length} of {allAvailableEmails.length}
                </div>
              </div>
            )}


            {isLoadingTemplate && (
              <div className="loading-template">
                <div className="loading-spinner">{t('components.email_modal.loading_email_template')}</div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email-subject">{t('common.subject')} *</label>
              <input
                id="email-subject"
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder={t('components.email_modal.enter_email_subject')}
                className="email-subject-input"
                disabled={isLoadingTemplate}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email-content">{t('common.email_content')} *</label>
              <textarea
                id="email-content"
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                placeholder={t('components.email_modal.enter_email_content')}
                rows={12}
                className="email-content-input"
                disabled={isLoadingTemplate}
              />
              <div className="email-preview">
                <div 
                  className="preview-content"
                  dangerouslySetInnerHTML={{ __html: emailContent }}
                />
              </div>
            </div>


            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </div>

          <div className="email-modal-footer">
            <button
              className="change-language-btn"
              onClick={handleChangeLanguage}
              disabled={isSending || isLoadingTemplate}
            >
              {currentLanguage === 'en' ? t('components.email_modal.german') : 
               currentLanguage === 'de' ? t('components.email_modal.turkish') : 
               t('components.email_modal.english')}
            </button>
            <button
              className="cancel-btn"
              onClick={handleBackToAnalysis}
              disabled={isSending}
            >
              {t('common.cancel')}
            </button>
            <button
              className="send-btn"
              onClick={sendEmail}
              disabled={isSending || isLoadingTemplate || !emailSubject || !emailSubject.trim() || !emailContent || !emailContent.trim()}
            >
              {isSending ? t('common.sending') : t('common.send') + ' ' + t('common.email')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analysisResult) {
    return (
      <div className="analysis-content">
        <div className="no-analysis">
          <p>{t('common.no_analysis_data')}</p>
          <button 
            onClick={handleAnalyzeRequest}
            disabled={isAnalyzing}
            className="analyze-btn"
          >
            {isAnalyzing ? t('common.analyzing') : t('common.analyze_request')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-content">
      <div className="analysis-results">
        {/* Analysis Summary */}
        <div className="analysis-summary">
          <h3>{t('common.analysis_summary')}</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>{t('common.ai_suppliers_found')}:</label>
              <span>{analysisResult.ai_suppliers?.length || 0}</span>
            </div>
            <div className="info-item">
              <label>{t('common.local_suppliers_found')}:</label>
              <span>{analysisResult.local_suppliers?.length || 0}</span>
            </div>
            <div className="info-item">
              <label>{t('common.analysis_date')}:</label>
              <span>{request.analyzed_at ? formatDate(request.analyzed_at) : formatDate(new Date().toISOString())}</span>
            </div>
          </div>
        </div>

        {/* Analysis Tabs */}
        <div className="analysis-tabs">
          <button 
            className={`analysis-tab-btn ${activeAnalysisTab === 'ai' ? 'active' : ''}`}
            onClick={() => setActiveAnalysisTab('ai')}
          >
            {t('common.ai_recommendations')} ({analysisResult.ai_suppliers?.length || 0})
          </button>
          <button 
            className={`analysis-tab-btn ${activeAnalysisTab === 'local' ? 'active' : ''}`}
            onClick={() => setActiveAnalysisTab('local')}
          >
            {t('common.local_database')} ({analysisResult.local_suppliers?.length || 0})
          </button>
          {request.selected_approved_suppliers && request.selected_approved_suppliers.length > 0 && (
            <button 
              className={`analysis-tab-btn ${activeAnalysisTab === 'approved' ? 'active' : ''}`}
              onClick={() => setActiveAnalysisTab('approved')}
            >
              {t('common.approved_suppliers')} ({request.selected_approved_suppliers.length})
            </button>
          )}
          <button 
            className={`analysis-tab-btn ${activeAnalysisTab === 'manual' ? 'active' : ''}`}
            onClick={() => setActiveAnalysisTab('manual')}
          >
            {t('common.manual_add')} ({manualSuppliers.length})
          </button>
        </div>

        {/* AI Suppliers */}
        {activeAnalysisTab === 'ai' && analysisResult.ai_suppliers && analysisResult.ai_suppliers.length > 0 && (
          <div className="suppliers-section">
            <div className="suppliers-header">
              <h4>{t('common.ai_recommended_suppliers')} ({analysisResult.ai_suppliers.length})</h4>
              <div className="filter-controls">
                <input
                  type="text"
                  placeholder={t('common.filter_by_region')}
                  value={filterCriteria.region}
                  onChange={(e) => setFilterCriteria({...filterCriteria, region: e.target.value})}
                  className="filter-input"
                />
                <button onClick={handleFilterSuppliers} className="filter-btn">{t('common.filter')}</button>
                <button onClick={clearFilters} className="clear-btn">{t('common.clear')}</button>
              </div>
            </div>
            <div className="suppliers-list">
              {(filteredSuppliers.length > 0 ? filteredSuppliers : analysisResult.ai_suppliers).map((supplier: any, index: number) => (
                <div key={index} className="supplier-card">
                  <div className="supplier-header">
                    <h5>{supplier.company_name || supplier.supplier_name}</h5>
                    {supplier.rating && (
                      <span className="rating">★ {supplier.rating}/5</span>
                    )}
                  </div>
                  <div className="supplier-details">
                    <div className="contact-info">
                      {supplier.website && <p><strong>{t('common.website')}:</strong> <a href={supplier.website} target="_blank" rel="noopener noreferrer">{supplier.website}</a></p>}
                      {supplier.email && <p><strong>{t('common.email')}:</strong> {supplier.email}</p>}
                      {supplier.phone && <p><strong>{t('common.phone')}:</strong> {supplier.phone}</p>}
                      {supplier.region && <p><strong>{t('common.region')}:</strong> {supplier.region}</p>}
                      {supplier.delivery_time && <p><strong>{t('common.delivery_time')}:</strong> {supplier.delivery_time}</p>}
                      {supplier.unit_price && <p><strong>{t('common.price')}:</strong> {supplier.unit_price} {supplier.currency || 'EUR'}</p>}
                    </div>
                    <div className="products-section">
                      <h6>{t('common.products')}:</h6>
                      {supplier.products && supplier.products.map((product: any, productIndex: number) => (
                        <div key={productIndex} className="product-item">
                          <p><strong>{t('common.name')}:</strong> {product.name}</p>
                          {product.brand && (
                            <p><strong>{t('common.brand')}:</strong> {product.brand}</p>
                          )}
                          {product.description && (
                            <p><strong>{t('common.description')}:</strong> {product.description}</p>
                          )}
                          <p><strong>{t('common.price')}:</strong> {product.price} {product.currency || supplier.currency}</p>
                          {product.min_order && (
                            <p><strong>{t('common.minimum_order')}:</strong> {product.min_order}</p>
                          )}
                          {product.availability && (
                            <p><strong>{t('common.availability')}:</strong> {product.availability}</p>
                          )}
                          {product.link && (
                            <p><strong>{t('common.link')}:</strong> 
                              <a href={product.link} target="_blank" rel="noopener noreferrer">
                                {product.link}
                              </a>
                            </p>
                          )}
                          {product.specifications && Object.keys(product.specifications).length > 0 && (
                            <div className="specifications">
                              <p><strong>{t('common.specifications')}:</strong></p>
                              <ul>
                                {Object.entries(product.specifications).map(([key, value]: [string, any]) => (
                                  <li key={key}><strong>{key}:</strong> {value}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <button 
                            className="edit-product-btn"
                            onClick={() => handleEditProduct(product, index, productIndex, 'ai_suppliers')}
                          >
                            ✏️ {t('common.edit')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="supplier-actions">
                    {supplier.email_sent ? (
                      <div className="email-sent-status">
                        <span className="email-sent-icon">✅</span>
                        <span className="email-sent-text">Email Sent</span>
                        {supplier.email_sent_at && (
                          <span className="email-sent-date">
                            ({new Date(supplier.email_sent_at).toLocaleDateString()})
                          </span>
                        )}
                      </div>
                    ) : (
                      <button 
                        className="email-btn"
                        onClick={() => handleSendEmail(supplier, index, 'ai')}
                        disabled={!supplier.email}
                      >
                        📧 {t('common.send_email')}
                      </button>
                    )}
                    <button 
                      className="add-db-btn"
                      onClick={() => handleAddToDatabase(supplier, index, 'ai')}
                    >
                      ➕ {t('common.add_to_database')}
                    </button>
                    <button 
                      className="edit-btn"
                      onClick={() => handleEditSupplier(supplier, index, 'ai_suppliers')}
                    >
                      ✏️ {t('common.edit')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Action Buttons */}
            <div className="load-more-section">
              <div className="action-buttons">
                {/* Load More Button */}
                <button 
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <>
                      <span className="loading-spinner"></span>
                      {t('common.loading_more_suppliers')}...
                    </>
                  ) : (
                    <>
                      🔍 {t('common.load_more_suppliers')}
                    </>
                  )}
                </button>
                
                {/* Brave Search Section */}
                <div className="brave-search-section">
                  <div className="brave-search-controls">
                    <label htmlFor="brave-region-select" className="brave-search-label">
                      🌍 {t('common.search_region')}:
                    </label>
                    <select
                      id="brave-region-select"
                      value={braveSearchRegion}
                      onChange={(e) => setBraveSearchRegion(e.target.value)}
                      className="brave-region-select"
                      disabled={isBraveSearching}
                    >
                      <option value="CH">🇨🇭 Switzerland (German)</option>
                      <option value="TR">🇹🇷 Turkey (Turkish)</option>
                      <option value="DE">🇩🇪 Germany (German)</option>
                      <option value="US">🇺🇸 USA (English)</option>
                      <option value="GB">🇬🇧 UK (English)</option>
                      <option value="FR">🇫🇷 France (French)</option>
                      <option value="IT">🇮🇹 Italy (Italian)</option>
                      <option value="ES">🇪🇸 Spain (Spanish)</option>
                      <option value="NL">🇳🇱 Netherlands (Dutch)</option>
                      <option value="CN">🇨🇳 China (Chinese)</option>
                      <option value="JP">🇯🇵 Japan (Japanese)</option>
                      <option value="KR">🇰🇷 Korea (Korean)</option>
                      <option value="BR">🇧🇷 Brazil (Portuguese)</option>
                      <option value="CA">🇨🇦 Canada (English)</option>
                      <option value="AU">🇦🇺 Australia (English)</option>
                      <option value="MX">🇲🇽 Mexico (Spanish)</option>
                      <option value="RU">🇷🇺 Russia (Russian)</option>
                      <option value="PL">🇵🇱 Poland (Polish)</option>
                      <option value="SE">🇸🇪 Sweden (Swedish)</option>
                      <option value="NO">🇳🇴 Norway (Norwegian)</option>
                      <option value="DK">🇩🇰 Denmark (Danish)</option>
                      <option value="FI">🇫🇮 Finland (Finnish)</option>
                      <option value="ASIA">🌏 Asia (English)</option>
                      <option value="EUROPE">🌍 Europe (English)</option>
                      <option value="AMERICA">🌎 America (English)</option>
                    </select>
                  </div>
                  
                  <button 
                    className="brave-search-btn"
                    onClick={handleBraveSearch}
                    disabled={isBraveSearching}
                  >
                    {isBraveSearching ? (
                      <>
                        <span className="loading-spinner"></span>
                        {t('common.brave_searching')}...
                      </>
                    ) : (
                      <>
                        🌐 {t('common.brave_search_suppliers')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Local Suppliers */}
        {activeAnalysisTab === 'local' && analysisResult.local_suppliers && analysisResult.local_suppliers.length > 0 && (
          <div className="suppliers-section">
            <div className="suppliers-header">
              <h4>{t('common.local_database_suppliers')} ({analysisResult.local_suppliers.length})</h4>
            </div>
            <div className="suppliers-list">
              {analysisResult.local_suppliers.map((item: any, index: number) => {
                const supplier = item.supplier;
                return (
                  <div key={index} className="supplier-card local-supplier">
                    <div className="supplier-header">
                      <h5>{supplier.name || supplier.company_name}</h5>
                      <span className="local-badge">Local DB</span>
                    </div>
                    <div className="supplier-details">
                      <div className="contact-info">
                        {supplier.email && <p><strong>{t('common.email')}:</strong> {supplier.email}</p>}
                        {supplier.phone && <p><strong>{t('common.phone')}:</strong> {supplier.phone}</p>}
                        {supplier.region && <p><strong>{t('common.region')}:</strong> {supplier.region}</p>}
                        {supplier.website && <p><strong>{t('common.website')}:</strong> <a href={supplier.website} target="_blank" rel="noopener noreferrer">{supplier.website}</a></p>}
                      </div>
                    </div>
                    <div className="supplier-actions">
                      <button 
                        className="email-btn"
                        onClick={() => handleSendEmail(supplier, index, 'ai')}
                        disabled={!supplier.email}
                      >
                        📧 {t('common.send_email')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Approved Suppliers */}
        {activeAnalysisTab === 'approved' && request.selected_approved_suppliers && request.selected_approved_suppliers.length > 0 && (
          <div className="suppliers-section">
            <div className="suppliers-header">
              <h4>{t('common.approved_suppliers')} ({request.selected_approved_suppliers.length})</h4>
            </div>
            <div className="suppliers-list">
              {request.selected_approved_suppliers.map((supplierName: string, index: number) => {
                // Check if email was sent for this approved supplier
                const emailSent = request.approved_supplier_emails_sent && 
                  request.approved_supplier_emails_sent[supplierName];
                
                return (
                  <div key={index} className="supplier-card approved-supplier">
                    <div className="supplier-header">
                      <h5>{supplierName}</h5>
                      <span className="approved-badge">Approved</span>
                    </div>
                    <div className="supplier-details">
                      <div className="contact-info">
                        <p><strong>{t('common.supplier_name')}:</strong> {supplierName}</p>
                        <p><strong>{t('common.status')}:</strong> {t('common.approved')}</p>
                        {emailSent && <p><strong>{t('common.email_sent')}:</strong> {formatDate(request.approved_supplier_emails_sent[supplierName])}</p>}
                      </div>
                    </div>
                    <div className="supplier-actions">
                      {emailSent ? (
                        <div className="email-sent-status">
                          <span className="email-sent-icon">✅</span>
                          <span className="email-sent-text">Email Sent</span>
                          {request.approved_supplier_emails_sent[supplierName] && (
                            <span className="email-sent-date">
                              ({new Date(request.approved_supplier_emails_sent[supplierName]).toLocaleDateString()})
                            </span>
                          )}
                        </div>
                      ) : (
                        <button 
                          className="email-btn"
                          onClick={() => handleSendEmail({
                            name: supplierName, 
                            company_name: supplierName,
                            email: supplierName, // Assuming supplier name is email for approved suppliers
                            email_sent: emailSent,
                            email_sent_at: emailSent ? request.approved_supplier_emails_sent[supplierName] : null,
                            index
                          }, index, 'approved')}
                          disabled={!supplierName}
                        >
                          📧 {t('common.send_email')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Manual Add Tab */}
        {activeAnalysisTab === 'manual' && (
          <div className="suppliers-section">
            <div className="suppliers-header">
              <div className="dropdown-header" onClick={() => setIsManualAddExpanded(!isManualAddExpanded)}>
                <h4>{t('common.manual_add_suppliers')}</h4>
                <span className={`dropdown-arrow ${isManualAddExpanded ? 'expanded' : ''}`}>
                  ▼
                </span>
              </div>
              <p>{t('common.manual_add_description')}</p>
            </div>
            
            {/* Manual Add Form - Collapsible */}
            {isManualAddExpanded && (
              <div className="manual-add-form">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.supplier_name')} *</label>
                  <input
                    type="text"
                    value={manualSupplierForm.supplier_name}
                    onChange={(e) => handleManualFormChange('supplier_name', e.target.value)}
                    placeholder={t('common.enter_supplier_name')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('common.supplier_email')}</label>
                  <input
                    type="email"
                    value={manualSupplierForm.supplier_email}
                    onChange={(e) => handleManualFormChange('supplier_email', e.target.value)}
                    placeholder={t('common.enter_supplier_email')}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.supplier_phone')}</label>
                  <input
                    type="text"
                    value={manualSupplierForm.supplier_phone}
                    onChange={(e) => handleManualFormChange('supplier_phone', e.target.value)}
                    placeholder={t('common.enter_supplier_phone')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('common.supplier_website')}</label>
                  <input
                    type="url"
                    value={manualSupplierForm.supplier_website}
                    onChange={(e) => handleManualFormChange('supplier_website', e.target.value)}
                    placeholder={t('common.enter_supplier_website')}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.product_name')} *</label>
                  <input
                    type="text"
                    value={manualSupplierForm.product_name}
                    onChange={(e) => handleManualFormChange('product_name', e.target.value)}
                    placeholder={t('common.enter_product_name')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('common.product_brand')}</label>
                  <input
                    type="text"
                    value={manualSupplierForm.product_brand}
                    onChange={(e) => handleManualFormChange('product_brand', e.target.value)}
                    placeholder={t('common.enter_product_brand')}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.price')} *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={manualSupplierForm.price}
                    onChange={(e) => handleManualFormChange('price', e.target.value)}
                    placeholder={t('common.enter_price')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('common.currency')}</label>
                  <select
                    value={manualSupplierForm.currency}
                    onChange={(e) => handleManualFormChange('currency', e.target.value)}
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="CHF">CHF</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('common.delivery_time')}</label>
                  <input
                    type="text"
                    value={manualSupplierForm.delivery_time}
                    onChange={(e) => handleManualFormChange('delivery_time', e.target.value)}
                    placeholder={t('common.delivery_time_placeholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('common.supplier_region')}</label>
                  <select
                    value={manualSupplierForm.supplier_region}
                    onChange={(e) => handleManualFormChange('supplier_region', e.target.value)}
                  >
                    <option value="TR">Turkey</option>
                    <option value="EU">Europe</option>
                    <option value="US">United States</option>
                    <option value="AS">Asia</option>
                  </select>
                </div>
              </div>

              <div className="form-group full-width">
                <label>{t('common.product_description')}</label>
                <textarea
                  value={manualSupplierForm.product_description}
                  onChange={(e) => handleManualFormChange('product_description', e.target.value)}
                  placeholder={t('common.enter_product_description')}
                  rows={3}
                />
              </div>

              <div className="form-group full-width">
                <label>{t('common.notes')}</label>
                <textarea
                  value={manualSupplierForm.notes}
                  onChange={(e) => handleManualFormChange('notes', e.target.value)}
                  placeholder={t('common.enter_additional_notes')}
                  rows={2}
                />
              </div>

              <div className="form-actions">
                <button
                  onClick={addManualSupplier}
                  disabled={isAddingManual}
                  className="add-manual-btn"
                >
                  {isAddingManual ? t('common.adding') : t('common.add_manual_supplier')}
                </button>
              </div>
            </div>
            )}

            {/* Manual Suppliers List */}
            {manualSuppliers.length > 0 && (
              <div className="manual-suppliers-list">
                <h5>{t('common.added_manual_suppliers')} ({manualSuppliers.length})</h5>
                <div className="suppliers-list">
                  {manualSuppliers.map((supplier, index) => (
                    <div key={supplier.id} className="supplier-card manual-supplier">
                      <div className="supplier-header">
                        <h5>{supplier.company_name}</h5>
                        <div className="supplier-badges">
                          <span className="manual-badge">Manual</span>
                          {supplier.email_sent && (
                            <span className="email-sent-badge">📧 {t('common.email_sent')}</span>
                          )}
                          <button
                            onClick={() => removeManualSupplier(supplier.id)}
                            className="remove-btn"
                            title={t('common.remove_supplier')}
                          >
                            ×
                          </button>
                        </div>
                      </div>
                      <div className="supplier-details">
                        <div className="contact-info">
                          {supplier.email && (
                            <p><strong>{t('common.email')}:</strong> {supplier.email}</p>
                          )}
                          {supplier.phone && (
                            <p><strong>{t('common.phone')}:</strong> {supplier.phone}</p>
                          )}
                          {supplier.website && (
                            <p><strong>{t('common.website')}:</strong> 
                              <a href={supplier.website} target="_blank" rel="noopener noreferrer">
                                {supplier.website}
                              </a>
                            </p>
                          )}
                          {supplier.region && (
                            <p><strong>{t('common.region')}:</strong> {supplier.region}</p>
                          )}
                        </div>
                        <div className="products-section">
                          <h6>{t('common.products')}:</h6>
                          {supplier.products && supplier.products.map((product: any, productIndex: number) => (
                            <div key={productIndex} className="product-item">
                              <p><strong>{t('common.name')}:</strong> {product.name}</p>
                              {product.brand && (
                                <p><strong>{t('common.brand')}:</strong> {product.brand}</p>
                              )}
                              {product.description && (
                                <p><strong>{t('common.description')}:</strong> {product.description}</p>
                              )}
                              <p><strong>{t('common.price')}:</strong> {product.price} {product.currency || supplier.currency}</p>
                              {product.min_order && (
                                <p><strong>{t('common.minimum_order')}:</strong> {product.min_order}</p>
                              )}
                              {product.availability && (
                                <p><strong>{t('common.availability')}:</strong> {product.availability}</p>
                              )}
                              {product.link && (
                                <p><strong>{t('common.link')}:</strong> 
                                  <a href={product.link} target="_blank" rel="noopener noreferrer">
                                    {product.link}
                                  </a>
                                </p>
                              )}
                              {product.specifications && Object.keys(product.specifications).length > 0 && (
                                <div className="specifications">
                                  <p><strong>{t('common.specifications')}:</strong></p>
                                  <ul>
                                    {Object.entries(product.specifications).map(([key, value]: [string, any]) => (
                                      <li key={key}><strong>{key}:</strong> {value}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {supplier.delivery_time && (
                          <p><strong>{t('common.delivery_time')}:</strong> {supplier.delivery_time}</p>
                        )}
                        {supplier.notes && (
                          <p><strong>{t('common.notes')}:</strong> {supplier.notes}</p>
                        )}
                      </div>
                      <div className="supplier-actions">
                        {supplier.email_sent ? (
                          <div className="email-sent-status">
                            <span className="email-sent-icon">✅</span>
                            <span className="email-sent-text">Email Sent</span>
                            {supplier.email_sent_at && (
                              <span className="email-sent-date">
                                ({new Date(supplier.email_sent_at).toLocaleDateString()})
                              </span>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              // Calculate global index for manual suppliers
                              const aiSuppliersCount = request.analysis_result?.ai_suppliers?.length || 0;
                              const localSuppliersCount = request.analysis_result?.local_suppliers?.length || 0;
                              const globalIndex = aiSuppliersCount + localSuppliersCount + index;
                              console.log(`🔍 DEBUG: Manual supplier email - local index: ${index}, global index: ${globalIndex}`);
                              handleSendEmail(supplier, globalIndex, 'manual');
                            }}
                            className="send-email-btn"
                            title={t('common.send_email')}
                            disabled={!supplier.email}
                          >
                            📧 {t('common.send_email')}
                          </button>
                        )}
                        <button
                          onClick={() => handleAddToDatabase(supplier, index, 'manual')}
                          className="add-to-db-btn"
                          title={t('common.add_to_database')}
                        >
                          💾 {t('common.add_to_database')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Supplier Database Modal */}
      {isSupplierModalOpen && (
        <SupplierDatabaseModal
          isOpen={isSupplierModalOpen}
          onClose={() => setIsSupplierModalOpen(false)}
          requestId={requestId}
          supplierIndex={selectedSupplierIndex}
          initialSupplierData={selectedSupplierForModal}
          onSupplierAdded={handleActionCompleted}
        />
      )}
      
      {/* Edit Modal */}
      {editModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-content edit-modal">
            <div className="modal-header">
              <h3>{editModal.type === 'supplier' ? t('common.edit_supplier') : t('common.edit_product')}</h3>
              <button 
                className="close-btn"
                onClick={() => setEditModal({ isOpen: false, type: 'supplier', data: null, supplierIndex: null, productIndex: null, supplierType: 'ai_suppliers' })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {editModal.type === 'supplier' ? (
                <div className="edit-form">
                  <div className="form-group">
                    <label>{t('common.company_name')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.company_name || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), company_name: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.email')}:</label>
                    <input
                      type="email"
                      value={editModal.data?.email || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), email: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.phone')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.phone || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), phone: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.website')}:</label>
                    <input
                      type="url"
                      value={editModal.data?.website || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), website: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.region')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.region || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), region: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.delivery_time')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.delivery_time || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), delivery_time: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.business_type')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.business_type || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), business_type: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.rating')}:</label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      value={editModal.data?.rating || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), rating: parseFloat(e.target.value) }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Additional Emails:</label>
                    <div className="additional-emails-section">
                      {(editModal.data?.additional_emails || []).map((email: string, index: number) => (
                        <div key={index} className="additional-email-item">
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => {
                              const newEmails = [...(editModal.data?.additional_emails || [])];
                              newEmails[index] = e.target.value;
                              setEditModal({
                                ...editModal,
                                data: { ...(editModal.data || {}), additional_emails: newEmails }
                              });
                            }}
                          />
                          <button
                            type="button"
                            className="remove-email-btn"
                            onClick={() => {
                              const newEmails = (editModal.data?.additional_emails || []).filter((_: any, i: number) => i !== index);
                              setEditModal({
                                ...editModal,
                                data: { ...(editModal.data || {}), additional_emails: newEmails }
                              });
                            }}
                          >
                            ❌
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="add-email-btn"
                        onClick={() => {
                          const newEmails = [...(editModal.data?.additional_emails || []), ''];
                          setEditModal({
                            ...editModal,
                            data: { ...(editModal.data || {}), additional_emails: newEmails }
                          });
                        }}
                      >
                        ➕ Add Email
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="edit-form">
                  <div className="form-group">
                    <label>{t('common.name')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.name || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), name: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.brand')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.brand || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), brand: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.description')}:</label>
                    <textarea
                      value={editModal.data?.description || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), description: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.price')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.price || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), price: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.currency')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.currency || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), currency: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.minimum_order')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.min_order || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), min_order: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.availability')}:</label>
                    <input
                      type="text"
                      value={editModal.data?.availability || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), availability: e.target.value }
                      })}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('common.link')}:</label>
                    <input
                      type="url"
                      value={editModal.data?.link || ''}
                      onChange={(e) => setEditModal({
                        ...editModal,
                        data: { ...(editModal.data || {}), link: e.target.value }
                      })}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-btn"
                onClick={() => setEditModal({ isOpen: false, type: 'supplier', data: null, supplierIndex: null, productIndex: null, supplierType: 'ai_suppliers' })}
              >
                {t('common.cancel')}
              </button>
              <button 
                className="save-btn"
                onClick={handleSaveEdit}
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestAnalysisTab;

// CSS Styles
const styles = `
  .email-sent-status {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    border-radius: 4px;
    color: #155724;
    font-size: 14px;
    font-weight: 500;
  }
  
  .email-sent-icon {
    font-size: 16px;
  }
  
  .email-sent-text {
    font-weight: 600;
  }
  
  .email-sent-date {
    font-size: 12px;
    opacity: 0.8;
  }
  
  .recipient-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    margin-bottom: 8px;
    background: white;
  }
  
  .recipient-item .email-address {
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: #374151;
    flex: 1;
  }
  
  .recipient-options {
    display: flex;
    gap: 16px;
  }
  
  .recipient-checkbox {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }
  
  .recipient-checkbox .checkbox-label {
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
  }
  
  .recipient-checkbox input[type="checkbox"] {
    margin: 0;
    accent-color: #3b82f6;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
